<template>
  <Dialog
    v-model:visible="showDialog"
    :header="
      !isEditable
        ? t('wkt.client.expensesheets.edit-item-dialog.view_title')
        : !data.id
          ? t('wkt.client.expensesheets.edit-item-dialog.new_title')
          : t('wkt.client.expensesheets.edit-item-dialog.edit_title')
    "
    :modal="true"
    :dismissableMask="!isMobile"
    :closable="false"
    :draggable="false"
    class="custom-dialog fixNotchPaddingTop"
    :style="{
      width: isMobile ? '100vw' : '90vw',
      maxWidth: isMobile ? '100vw' : '900px',
    }"
    :breakpoints="{ '768px': '100vw' }"
  >
    <ExpenseSheetItemSkeleton v-if="isLoading" />

    <!-- Contenido principal -->
    <div v-if="!isLoading" class="item-content">
      <form @submit.prevent="save">
        <div class="form-grid form-content">
          <!-- Hoja de gastos -->
          <div class="form-field">
            <label class="field-label">
              {{ t("wkt.client.expensesheets.edit-item-dialog.expense_sheet") }}
            </label>
            <Dropdown
              v-model="data.sheetId"
              :options="sheets"
              optionLabel="name"
              optionValue="id"
              :placeholder="
                t(
                  'wkt.client.expensesheets.edit-item-dialog.expense_sheet_placeholder'
                )
              "
              :disabled="!isEditable"
              :class="{ 'p-invalid': formErrors.sheetId }"
              @change="formErrors.sheetId = null"
            />
            <FieldError :error="formErrors.sheetId" />
          </div>

          <!-- Categoría -->
          <div class="form-field">
            <label class="field-label">
              {{ t("wkt.client.expensesheets.edit-item-dialog.category") }}
            </label>
            <Dropdown
              v-model="data.categoryId"
              :options="categories"
              optionLabel="name"
              optionValue="id"
              :placeholder="
                t(
                  'wkt.client.expensesheets.edit-item-dialog.category_placeholder'
                )
              "
              :disabled="!isEditable"
              :class="{ 'p-invalid': formErrors.categoryId }"
              @change="formErrors.categoryId = null"
            />
            <FieldError :error="formErrors.categoryId" />
          </div>

          <!-- Tipo de formato -->
          <div class="form-field">
            <label class="field-label">
              {{ t("wkt.client.expensesheets.edit-item-dialog.format_type") }}
            </label>
            <Dropdown
              v-model="formatType"
              :options="formatTypes"
              optionLabel="name"
              :placeholder="
                t(
                  'wkt.client.expensesheets.edit-item-dialog.format_type_placeholder'
                )
              "
              :disabled="!isEditable"
              :class="{ 'p-invalid': formErrors.formatType }"
              @change="onFormatTypeChange"
            />
            <FieldError :error="formErrors.formatType" />
          </div>

          <!-- Método de pago -->
          <div class="form-field">
            <label class="field-label">
              {{
                t("wkt.client.expensesheets.edit-item-dialog.payment_method")
              }}
            </label>
            <Dropdown
              v-model="paymentMethod"
              :options="paymentMethods"
              optionLabel="name"
              :placeholder="
                t(
                  'wkt.client.expensesheets.edit-item-dialog.payment_method_placeholder'
                )
              "
              :disabled="!isEditable"
              :class="{ 'p-invalid': formErrors.paymentMethod }"
              @change="formErrors.paymentMethod = null"
            />
            <FieldError :error="formErrors.paymentMethod" />
          </div>

          <!-- Fecha del ticket -->
          <div class="form-field">
            <label class="field-label">
              {{ t("wkt.client.expensesheets.edit-item-dialog.ticket_date") }}
            </label>
            <InputText
              v-model="ticketDateFormatted"
              :placeholder="
                t(
                  'wkt.client.expensesheets.edit-item-dialog.ticket_date_placeholder'
                )
              "
              readonly
              :disabled="!isEditable"
              :class="{ 'p-invalid': formErrors.ticketDate }"
              @click="isEditable ? showDateTimeDialog() : null"
            />
            <FieldError :error="formErrors.ticketDate" />
          </div>

          <!-- Descripción del gasto -->
          <div class="form-field">
            <label class="field-label">
              {{
                t(
                  "wkt.client.expensesheets.edit-item-dialog.expense_description"
                )
              }}
            </label>
            <InputText
              v-model="data.expenseDescription"
              :placeholder="
                t(
                  'wkt.client.expensesheets.edit-item-dialog.expense_description_placeholder'
                )
              "
              :disabled="!isEditable"
              maxlength="255"
              :class="{ 'p-invalid': formErrors.expenseDescription }"
              @input="formErrors.expenseDescription = null"
            />
            <FieldError :error="formErrors.expenseDescription" />
          </div>

          <!-- Campos solo para facturas -->
          <template v-if="onlyForInvoice">
            <!-- NIF del proveedor -->
            <div class="form-field">
              <label class="field-label">
                {{
                  t(
                    "wkt.client.expensesheets.edit-item-dialog.vendor_vat_number"
                  )
                }}
              </label>
              <InputText
                v-model="data.vendorVatNumber"
                :placeholder="
                  t(
                    'wkt.client.expensesheets.edit-item-dialog.vendor_vat_number_placeholder'
                  )
                "
                :disabled="!isEditable"
                maxlength="18"
                :class="{ 'p-invalid': formErrors.vendorVatNumber }"
                @input="formErrors.vendorVatNumber = null"
              />
              <FieldError :error="formErrors.vendorVatNumber" />
            </div>

            <!-- Nombre del proveedor -->
            <div class="form-field">
              <label class="field-label">
                {{ t("wkt.client.expensesheets.edit-item-dialog.vendor_name") }}
              </label>
              <InputText
                v-model="data.vendorName"
                :placeholder="
                  t(
                    'wkt.client.expensesheets.edit-item-dialog.vendor_name_placeholder'
                  )
                "
                :disabled="!isEditable"
                maxlength="255"
                :class="{ 'p-invalid': formErrors.vendorName }"
                @input="formErrors.vendorName = null"
              />
              <FieldError :error="formErrors.vendorName" />
            </div>

            <!-- Porcentaje de impuestos -->
            <div class="form-field">
              <label class="field-label">
                {{
                  t("wkt.client.expensesheets.edit-item-dialog.taxes_percent")
                }}
              </label>
              <InputText
                v-model="taxesPercent"
                :placeholder="
                  t(
                    'wkt.client.expensesheets.edit-item-dialog.taxes_percent_placeholder'
                  )
                "
                :disabled="!isEditable"
                :class="{ 'p-invalid': formErrors.taxesPercent }"
                @input="formErrors.taxesPercent = null"
              />
              <FieldError :error="formErrors.taxesPercent" />
            </div>

            <!-- Importe de impuestos -->
            <div class="form-field">
              <label class="field-label">
                {{
                  t("wkt.client.expensesheets.edit-item-dialog.taxes_amount")
                }}
              </label>
              <InputText
                v-model="taxesAmount"
                :placeholder="
                  t(
                    'wkt.client.expensesheets.edit-item-dialog.taxes_amount_placeholder'
                  )
                "
                :disabled="!isEditable"
                :class="{ 'p-invalid': formErrors.taxesAmount }"
                @input="formErrors.taxesAmount = null"
              />
              <FieldError :error="formErrors.taxesAmount" />
            </div>

            <!-- Importe bruto -->
            <div class="form-field">
              <label class="field-label">
                {{
                  t("wkt.client.expensesheets.edit-item-dialog.gross_amount")
                }}
              </label>
              <InputText
                v-model="grossAmount"
                :placeholder="
                  t(
                    'wkt.client.expensesheets.edit-item-dialog.gross_amount_placeholder'
                  )
                "
                :disabled="!isEditable"
                :class="{ 'p-invalid': formErrors.grossAmount }"
                @input="formErrors.grossAmount = null"
              />
              <FieldError :error="formErrors.grossAmount" />
            </div>
          </template>

          <!-- Importe total -->
          <div class="form-field">
            <label class="field-label">
              {{ t("wkt.client.expensesheets.edit-item-dialog.total_amount") }}
            </label>
            <InputText
              v-model="totalAmount"
              :placeholder="
                t(
                  'wkt.client.expensesheets.edit-item-dialog.total_amount_placeholder'
                )
              "
              :disabled="!isEditable"
              :class="{ 'p-invalid': formErrors.totalAmount }"
              @input="formErrors.totalAmount = null"
            />
            <FieldError :error="formErrors.totalAmount" />
          </div>

          <!-- Número de ticket (solo facturas) -->
          <div v-if="onlyForInvoice" class="form-field full-width">
            <label class="field-label">
              {{ t("wkt.client.expensesheets.edit-item-dialog.ticket_number") }}
            </label>
            <InputText
              v-model="data.ticketNumber"
              :placeholder="
                t(
                  'wkt.client.expensesheets.edit-item-dialog.ticket_number_placeholder'
                )
              "
              :disabled="!isEditable"
              maxlength="50"
              :class="{ 'p-invalid': formErrors.ticketNumber }"
              @input="formErrors.ticketNumber = null"
            />
            <FieldError :error="formErrors.ticketNumber" />
          </div>

          <!-- Descripción (solo facturas) -->
          <div v-if="onlyForInvoice" class="form-field full-width">
            <label class="field-label">
              {{ t("wkt.client.expensesheets.edit-item-dialog.description") }}
            </label>
            <Textarea
              v-model="data.description"
              :placeholder="
                t(
                  'wkt.client.expensesheets.edit-item-dialog.description_placeholder'
                )
              "
              :disabled="!isEditable"
              rows="3"
              maxlength="4000"
            />
          </div>
        </div>

        <!-- Botón para adjuntar archivo (móvil) -->
        <div
          v-if="isEditable && !file && !data.documentPath && isMobile"
          class="attach-section-mobile"
        >
          <h3 class="attach-title">
            {{ t("wkt.client.expensesheets.edit-item-dialog.attached_file") }}
          </h3>
          <Button
            icon="pi pi-cloud-upload"
            :label="t('wkt.client.common.dialog-buttons.attach_file')"
            class="primary-button"
            :disabled="isUpdating"
            @click="attachDocument()"
          />
          <input
            ref="uploaderInput"
            class="hidden-input"
            type="file"
            accept="image/*,.png,.jpg,.jpeg,.heic,.heif,image/heic,image/heif,image/jpeg,image/png,image/webp,image/jpg"
            @change="onFileChanged"
          />
        </div>

        <!-- Documento adjunto: mostrar si hay archivo seleccionado o documento existente -->
        <div v-if="file || data.documentPath" class="document-section">
          <h3 class="document-title">
            {{ t("wkt.client.expensesheets.edit-item-dialog.attached_file") }}
          </h3>

          <!-- Preview Image -->
          <div
            v-if="base64Image"
            class="document-preview"
            ref="previewImageContainer"
          >
            <Image
              :src="base64Image"
              alt="Ticket"
              preview
              :pt="{
                image: {
                  style: 'max-width: 300px; height: auto; object-fit: contain;',
                },
                mask: {
                  class: 'fixNotchPaddingTop',
                },
                previewImage: {
                  style:
                    'touch-action: none; -webkit-user-drag: none; -ms-touch-action: none; user-select: none;',
                  onTouchstart: handleTouchStart,
                  onTouchmove: handleTouchMove,
                  onTouchend: handleTouchEnd,
                },
              }"
            >
              <template #previewicon>
                <i class="pi pi-eye"></i>
              </template>
            </Image>
          </div>

          <div class="document-info">
            <span class="document-name">{{
              file ? file.name : data.documentName
            }}</span>
            <Button
              v-if="isEditable"
              icon="pi pi-trash"
              text
              severity="danger"
              size="small"
              @click="deleteExpenseSheetItemDocument()"
            />
          </div>
        </div>
      </form>
    </div>

    <!-- Footer con template -->
    <template #footer>
      <div v-if="!isLoading" class="dialog-footer-content">
        <Button
          v-if="isEditable && data.id"
          :label="t('wkt.client.common.dialog-buttons.delete')"
          class="uppercase"
          text
          severity="danger"
          @click="confirmDelete()"
        />

        <Button
          v-if="isEditable && !file && !data.documentPath && !isMobile"
          :label="t('wkt.client.common.dialog-buttons.attach_file')"
          class="footer-primary-button"
          color="#185EA9"
          variant="text"
          :disabled="isUpdating"
          @click="attachDocument()"
        />
        <input
          v-if="!isMobile"
          ref="uploaderInput"
          class="hidden-input"
          type="file"
          accept="image/*,.png,.jpg,.jpeg,.heic,.heif,image/heic,image/heif,image/jpeg,image/png,image/webp,image/jpg"
          @change="onFileChanged"
        />

        <div class="spacer"></div>

        <Button
          v-if="isEditable"
          :label="t('wkt.client.common.dialog-buttons.ok')"
          class="footer-primary-button"
          color="#185EA9"
          variant="text"
          @click="save()"
        />

        <Button
          :label="
            isEditable
              ? t('wkt.client.common.dialog-buttons.cancel')
              : t('wkt.client.common.dialog-buttons.close')
          "
          class="footer-button-cancel"
          variant="text"
          @click="close()"
        />
      </div>
    </template>

    <!-- Overlay de previsualización de imagen -->
    <div
      v-if="imagePreviewVisible"
      class="image-preview-overlay"
      @click.self="closeImagePreview()"
    >
      <img
        v-if="base64Image"
        :src="base64Image"
        alt="Preview"
        class="image-preview-full"
      />
    </div>
  </Dialog>

  <!-- Diálogos auxiliares -->
  <ReadingTicketDialog ref="dlgReadingTicket" />
  <ConfirmTicketDialog ref="dlgConfirmTicket" />
  <SelectDateTimeDialog ref="dlgSelectDateTime" />

  <ConfirmDialog
    ref="dlgConfirm"
    :title="t('wkt.client.expensesheets.edit-item-dialog.delete_title')"
    :message="t('wkt.client.expensesheets.edit-item-dialog.delete_text')"
    :titleOk="t('wkt.client.common.dialog-buttons.delete')"
    :titleCancel="t('wkt.client.common.dialog-buttons.cancel')"
    @confirmed="deleteItem(true)"
  />
</template>

<script setup lang="ts">
import expenseSheetsApi from "@wkt/api/expense-sheets";
import { gwtApi } from "@wkt/api/gwt/gwtAPI";
import FieldError from "@wkt/components/common/FieldError.vue";
import SelectDateTimeDialog from "@wkt/components/common/SelectDateTimeDialog.vue";
import ConfirmTicketDialog from "@wkt/components/modules/expensesheets/ConfirmTicketDialog.vue";
import ReadingTicketDialog from "@wkt/components/modules/expensesheets/ReadingTicketDialog.vue";
import ExpenseSheetItemSkeleton from "@wkt/components/modules/skeletons/expensesheets/ExpenseSheetItemSkeleton.vue";
import { useAlert } from "@wkt/composables/useAlert";
import { useDateFormatter } from "@wkt/composables/useDateFormatter";
import { useFileConverter } from "@wkt/composables/useFileConverter";
import { useUtils } from "@wkt/composables/useUtils";
import Image from "primevue/image";
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import ConfirmDialog from "../../common/ConfirmDialog.vue";

// Composables
const { t } = useI18n();
const { formatLongDateTime } = useDateFormatter();
const { formatCurrency, formatNumberToLocale } = useUtils();
const { heicToJpg } = useFileConverter();
const { showAlert } = useAlert();

// State
const showDialog = ref(false);
const isLoading = ref(false);
const isEditable = ref(false);
const isMobile = ref(false);
const data = ref<any>({});
const taxesPercent = ref<string | null>(null);
const taxesAmount = ref<string | null>(null);
const grossAmount = ref<string | null>(null);
const totalAmount = ref<string | null>(null);
const documentClicked = ref(false);
const ticketDateFormatted = ref<string | null>(null);
const isUpdating = ref(false);
const file = ref<File | null>(null);
const msgCbUpdated = ref<(() => void) | null>(null);
const msgCbDeleted = ref<(() => void) | null>(null);
const sheets = ref<any[]>([]);
const categories = ref<any[]>([]);
const formatType = ref<any>(null);
const paymentMethod = ref<any>(null);
const base64Image = ref<string | null>(null);
const onlyForInvoice = ref(false);
// Trackear si el item fue creado durante esta sesión del diálogo
const wasCreatedInThisSession = ref(false);
const originalItemId = ref<number | null>(null);
const currentSheetId = ref<number | null>(null);
const isAndroid = /Android/.test(navigator.userAgent);
const androidVersion = Number.parseFloat(
  /Android (\d+)/.exec(navigator.userAgent)?.[1] || "0"
);
// Flag para evitar múltiples ejecuciones del parche Android 12
const android12PatchApplied = ref(false);

// Reglas de validación (como en BaseModule.back)
const locale = navigator.language || "en";
const validationRules = {
  required: (v: unknown) =>
    !!v || t("wkt.client.common.validation-utils.required_field"),
  decimalSeparator: (v: string) =>
    !v ||
    gwtApi.validateDecimalSeparator(v, locale) ||
    t("wkt.client.common.validation-utils.invalid_field"),
  // Validación de separador decimal sin límite de decimales (para impuestos %)
  decimalSeparatorNoLimit: (v: string) => {
    if (!v) return true;

    // Eliminar espacios y guiones al inicio
    const cleanValue = v.toString().trim().replace(/^-/, "");

    // Si es solo dígitos, es válido
    if (/^\d+$/.test(cleanValue)) {
      return true;
    }

    // Verificar si hay más de un separador decimal
    const decimalSeparatorRegex = /([,.]).*\1/;
    if (decimalSeparatorRegex.test(cleanValue)) {
      return t("wkt.client.common.validation-utils.invalid_field");
    }

    // Obtener el separador decimal del locale
    const numFormat = new Intl.NumberFormat(locale);
    const decimalSeparator = numFormat.formatToParts(1.1)[1].value;

    // Encontrar la última aparición del separador decimal
    const lastIndex = cleanValue.lastIndexOf(decimalSeparator);
    if (lastIndex === -1) {
      return t("wkt.client.common.validation-utils.invalid_field");
    }

    // Obtener la parte decimal
    const decimalPart = cleanValue.substring(lastIndex + 1);

    // Verificar si la parte decimal es un número
    if (!isNaN(Number(decimalPart))) {
      return true;
    }

    return t("wkt.client.common.validation-utils.invalid_field");
  },
  isValidRange: (v: string) => {
    const num = Number.parseInt(v.replace(",", ".").replace(/[^0-9.]/g, ""));
    return !Number.isNaN(num) && num >= 0 && num <= 100
      ? true
      : t("wkt.client.common.validation-utils.out_of_range_field");
  },
  isGreaterThanZero: (v: string) => {
    if (!v || v.trim() === "") return false;
    const normalized = v.replace(",", ".").replace(/[^0-9.]/g, "");
    const num = Number.parseFloat(normalized);
    return !Number.isNaN(num) && num > 0;
  },
};

// Control de carga - flags para saber qué está listo
const loadingFlags = ref({
  sheets: false,
  categories: false,
  itemData: false,
  document: false,
});

// Form errors - ahora almacenan el mensaje de error en lugar de boolean
const formErrors = ref<any>({
  sheetId: null,
  categoryId: null,
  formatType: null,
  paymentMethod: null,
  ticketDate: null,
  expenseDescription: null,
  vendorVatNumber: null,
  vendorName: null,
  taxesPercent: null,
  taxesAmount: null,
  grossAmount: null,
  totalAmount: null,
  ticketNumber: null,
});

// Refs
const dlgReadingTicket = ref<InstanceType<typeof ReadingTicketDialog> | null>(
  null
);
const dlgConfirmTicket = ref<InstanceType<typeof ConfirmTicketDialog> | null>(
  null
);
const dlgSelectDateTime = ref<InstanceType<typeof SelectDateTimeDialog> | null>(
  null
);
const dlgConfirm = ref<InstanceType<typeof ConfirmDialog> | null>(null);
const uploaderInput = ref<HTMLInputElement | null>(null);

// Función helper para formatear decimales sin límite
function formatDecimalNoLimit(value: unknown): string {
  if (value === null || value === undefined) return "";

  const stringValue = String(value);
  const localeStr = locale || "en";

  // Obtener el separador decimal del locale
  const numFormat = new Intl.NumberFormat(localeStr, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const decimalSeparator = numFormat.formatToParts(1.1)[1].value;

  // Convertir el separador según el locale
  if (decimalSeparator === "." && stringValue.indexOf(",") !== -1) {
    return stringValue.replace(/,/g, ".");
  } else if (decimalSeparator === "," && stringValue.indexOf(".") !== -1) {
    return stringValue.replace(/\./g, ",");
  }

  return stringValue;
}

// Computed
const formatTypes = computed(() => [
  {
    id: 0,
    name: t("wkt.client.expensesheets.detail-window.items.format-type.ticket"),
  },
  {
    id: 1,
    name: t("wkt.client.expensesheets.detail-window.items.format-type.invoice"),
  },
]);

const paymentMethods = computed(() => [
  {
    id: 0,
    name: t(
      "wkt.client.expensesheets.detail-window.items.payment-method-type.cash"
    ),
  },
  {
    id: 1,
    name: t(
      "wkt.client.expensesheets.detail-window.items.payment-method-type.company_card"
    ),
  },
  {
    id: 2,
    name: t(
      "wkt.client.expensesheets.detail-window.items.payment-method-type.other"
    ),
  },
]);

// Methods
function checkMobile() {
  isMobile.value = window.innerWidth < 768;
}

// Variable para controlar la visibilidad del overlay de previsualización
const imagePreviewVisible = ref(false);
// Referencia al contenedor de la imagen preview para acceso directo al DOM
const previewImageContainer = ref<HTMLElement | null>(null);

function closeImagePreview() {
  imagePreviewVisible.value = false;
}

// Zoom con gestos táctiles (pinch-to-zoom)
let initialDistance = 0;
let startMidpoint = { x: 0, y: 0 };
let currentScale = 1;
let currentX = 0;
let currentY = 0;
let lastTouch = { x: 0, y: 0 };
let isPanning = false;

function getDistance(touches: TouchList): number {
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

function getMidpoint(touches: TouchList) {
  const x = (touches[0].clientX + touches[1].clientX) / 2;
  const y = (touches[0].clientY + touches[1].clientY) / 2;
  return { x, y };
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function applyTransform(target: HTMLElement) {
  target.style.transition = "transform 0s";
  target.style.transform = `translate(${currentX}px, ${currentY}px) scale(${currentScale})`;
}

function getGestureTarget(event?: TouchEvent) {
  if (previewImageContainer.value) {
    const img = previewImageContainer.value.querySelector("img");
    if (img) return img as HTMLElement;
    return previewImageContainer.value;
  }
  if (event && (event.currentTarget as HTMLElement)) {
    return event.currentTarget as HTMLElement;
  }
  return null;
}

function handleTouchStart(event: TouchEvent) {
  // Si hay dos dedos, comenzamos pinch
  if (event.touches.length === 2) {
    initialDistance = getDistance(event.touches);
    startMidpoint = getMidpoint(event.touches);
    isPanning = false;
  } else if (event.touches.length === 1 && currentScale > 1) {
    // Un dedo: comenzar pan si estamos zoomed
    lastTouch.x = event.touches[0].clientX;
    lastTouch.y = event.touches[0].clientY;
    isPanning = true;
  }
}

function handleTouchMove(event: TouchEvent) {
  const target = getGestureTarget(event) as HTMLElement | null;
  if (!target) return;

  if (event.touches.length === 2 && initialDistance > 0) {
    event.preventDefault();
    const currentDistance = getDistance(event.touches);
    const scaleDelta = currentDistance / initialDistance;
    const newScale = clamp(currentScale * scaleDelta, 1, 3);

    // Calcular el desplazamiento necesario para mantener el punto medio
    const mid = getMidpoint(event.touches);
    const dx = mid.x - startMidpoint.x;
    const dy = mid.y - startMidpoint.y;

    // Actualizar transform relativas
    applyTransform(target);
    // Aplicar escala primero sin afectar la posición acumulada
    target.style.transformOrigin = "0 0";
    // Ajustar currentX/Y para que el zoom ocurra centrado en el midpoint
    const rect = target.getBoundingClientRect();
    const offsetX = (mid.x - rect.left) * (1 - newScale / currentScale);
    const offsetY = (mid.y - rect.top) * (1 - newScale / currentScale);

    currentX = clamp(
      currentX + dx + offsetX,
      -rect.width * (newScale - 1),
      rect.width * (newScale - 1)
    );
    currentY = clamp(
      currentY + dy + offsetY,
      -rect.height * (newScale - 1),
      rect.height * (newScale - 1)
    );

    currentScale = newScale;

    target.style.transform = `translate(${currentX}px, ${currentY}px) scale(${currentScale})`;

    // Reset initialDistance/startMidpoint para que cada frame compare incrementalmente
    initialDistance = currentDistance;
    startMidpoint = mid;
  } else if (event.touches.length === 1 && isPanning && currentScale > 1) {
    event.preventDefault();
    const t = event.touches[0];
    const dx = t.clientX - lastTouch.x;
    const dy = t.clientY - lastTouch.y;

    const rect = target.getBoundingClientRect();
    currentX = clamp(
      currentX + dx,
      -rect.width * (currentScale - 1),
      rect.width * (currentScale - 1)
    );
    currentY = clamp(
      currentY + dy,
      -rect.height * (currentScale - 1),
      rect.height * (currentScale - 1)
    );

    applyTransform(target);
    lastTouch.x = t.clientX;
    lastTouch.y = t.clientY;
  }
}

function handleTouchEnd(event: TouchEvent) {
  const target =
    (getGestureTarget(event) as HTMLElement | null) ||
    (event.target as HTMLElement | null);
  if (event.touches.length === 0) {
    // Fin de gesto: si el scale está cerca de 1, resetear
    if (currentScale < 1.1) {
      currentScale = 1;
      currentX = 0;
      currentY = 0;
    }
    if (target) {
      target.style.transition = "transform 150ms ease";
      target.style.transform = `translate(${currentX}px, ${currentY}px) scale(${currentScale})`;
    }
    initialDistance = 0;
    isPanning = false;
  } else if (event.touches.length === 1) {
    // Queda un dedo: preparar pan
    lastTouch.x = event.touches[0].clientX;
    lastTouch.y = event.touches[0].clientY;
    isPanning = currentScale > 1;
  }
}

function open(
  id: number | null,
  sheetId: number,
  editable: boolean,
  cbUpdated?: () => void,
  cbDeleted?: () => void
) {
  isEditable.value = editable;
  taxesPercent.value = null;
  taxesAmount.value = null;
  grossAmount.value = null;
  totalAmount.value = null;
  formatType.value = null;
  paymentMethod.value = null;
  ticketDateFormatted.value = null;
  msgCbUpdated.value = cbUpdated || null;
  msgCbDeleted.value = cbDeleted || null;
  file.value = null;
  isUpdating.value = false;
  onlyForInvoice.value = true;
  documentClicked.value = false;
  // Guardar el ID original para saber si es nuevo o existente
  originalItemId.value = id;
  currentSheetId.value = sheetId;
  wasCreatedInThisSession.value = false;
  // Resetear el flag del parche Android 12 para nuevo documento
  android12PatchApplied.value = false;

  // MOSTRAR el diálogo inmediatamente con skeleton
  showDialog.value = true;
  isLoading.value = true;

  // Resetear flags de carga
  loadingFlags.value = {
    sheets: false,
    categories: false,
    itemData: !id, // Si no hay ID, no necesitamos cargar item
    document: true, // Asumimos que no hay documento hasta saber lo contrario
  };

  // Resetear errores
  for (const key of Object.keys(formErrors.value)) {
    formErrors.value[key] = null;
  }

  showInvoiceFields(false);

  getExpenseSheets();
  getExpenseSheetCategories();

  if (!id) {
    data.value = { sheetId };
    // checkLoadingComplete() se llamará cuando sheets y categories estén listos
    return;
  }

  // Para items existentes, inicializar sheetId por si acaso
  data.value.sheetId = sheetId;
  getExpenseSheetItemById(id);
}

function close() {
  // Si el item fue creado en esta sesión (al adjuntar archivo) y el usuario cancela,
  // eliminamos el item huérfano de la base de datos
  if (wasCreatedInThisSession.value && data.value.id && !originalItemId.value) {
    deleteItem(false);
    return;
  }
  showDialog.value = false;
}

function getExpenseSheets() {
  const sessionInfo = gwtApi.getSessionInformation();
  const companyId = sessionInfo?.company?.id;

  if (!companyId) {
    isLoading.value = false;
    showAlert({
      text: "No se pudo obtener la información de la empresa",
      color: "error",
    });
    return;
  }

  expenseSheetsApi.getExpenseSheets(
    { companyId, sortBy: "id", sortDesc: false },
    (response) => {
      // Solo las hojas con estado "EN PREPARACIÓN", "DENEGADAS" son editables
      const editableSheets = response.filter((e: any) =>
        [-1, 2].includes(e.status)
      );

      // Si hay una hoja asignada al item actual y no está en la lista de editables, agregarla
      if (data.value.sheetId) {
        const currentSheet = response.find(
          (e: any) => e.id === data.value.sheetId
        );
        if (
          currentSheet &&
          !editableSheets.find((e: any) => e.id === currentSheet.id)
        ) {
          editableSheets.push(currentSheet);
        }
      }

      sheets.value = editableSheets;
      loadingFlags.value.sheets = true;
      checkLoadingComplete();
    },
    (err: any) => {
      isLoading.value = false;
      console.error("Error al cargar hojas de gastos:", err);
      showAlert({
        text: err.message || "Error al cargar hojas de gastos",
        color: "error",
      });
    }
  );
}

function getExpenseSheetCategories() {
  expenseSheetsApi.getExpenseSheetCategories(
    (response) => {
      categories.value = response;
      loadingFlags.value.categories = true;
      checkLoadingComplete();
    },
    (err: any) => {
      isLoading.value = false;
      console.error("Error al cargar categorías:", err);
      showAlert({
        text: err.message || "Error al cargar categorías",
        color: "error",
      });
    }
  );
}

function checkLoadingComplete() {
  // Comprobar si todos los recursos necesarios están cargados
  const allLoaded = Object.values(loadingFlags.value).every(Boolean);

  if (allLoaded) {
    finishLoading();
  }
}

function getExpenseSheetItemById(id: number) {
  expenseSheetsApi.getExpenseSheetItemById(
    id,
    (response) => {
      data.value = response;

      formatType.value = formatTypes.value.find(
        (f) => f.id === response.formatType
      );
      paymentMethod.value = paymentMethods.value.find(
        (p) => p.id === response.paymentMethod
      );

      showInvoiceFields(formatType.value?.id === 1);

      if (data.value.ticketDate) {
        ticketDateFormatted.value = formatLongDateTime(data.value.ticketDate);
      }

      if (response.taxesPercent) {
        taxesPercent.value = formatDecimalNoLimit(response.taxesPercent);
      }
      if (response.taxesAmount) {
        taxesAmount.value = formatCurrency(response.taxesAmount);
      }
      if (response.grossAmount) {
        grossAmount.value = formatCurrency(response.grossAmount);
      }
      if (response.totalAmount) {
        totalAmount.value = formatCurrency(response.totalAmount);
      }

      loadingFlags.value.itemData = true;

      if (data.value.documentPath) {
        loadingFlags.value.document = false; // Necesitamos cargar el documento
        downloadDocumentContent();
      } else {
        // No hay documento que cargar
        loadingFlags.value.document = true;
        checkLoadingComplete();
      }
    },
    (err: any) => {
      isLoading.value = false;
      console.error("Error al cargar item:", err);
      showAlert({
        text: err.message || "Error al cargar item",
        color: "error",
      });
    }
  );
}

function finishLoading() {
  isLoading.value = false;
  showDialog.value = true;
}

function showInvoiceFields(show: boolean) {
  onlyForInvoice.value = show;
}

function onFormatTypeChange() {
  formErrors.value.formatType = null;
  if (formatType.value) {
    showInvoiceFields(formatType.value.id === 1);
  }
}

function showDateTimeDialog() {
  if (dlgSelectDateTime.value) {
    dlgSelectDateTime.value.open(
      data.value.ticketDate,
      undefined,
      (date: Date) => {
        data.value.ticketDate = date.getTime();
        ticketDateFormatted.value = formatLongDateTime(data.value.ticketDate);
        formErrors.value.ticketDate = null;
      }
    );
  }
}

function downloadDocumentContent() {
  /*
  console.log("📥 Descargando contenido del documento...", {
    sheetId: data.value.sheetId,
    itemId: data.value.id,
    documentPath: data.value.documentPath,
  });
  */

  expenseSheetsApi.downloadExpenseSheetItemDocumentContent(
    data.value.sheetId,
    data.value.id,
    (response: string) => {
      // Agregar prefijo MIME si no está presente
      if (response && !response.startsWith("data:image/")) {
        // Detectar tipo MIME basado en el nombre del archivo
        const fileName = data.value.documentName || "";
        let mimeType = "image/jpeg"; // Por defecto

        if (fileName.endsWith(".png")) {
          mimeType = "image/png";
        } else if (fileName.endsWith(".webp")) {
          mimeType = "image/webp";
        } else if (fileName.endsWith(".gif")) {
          mimeType = "image/gif";
        } else if (fileName.endsWith(".pdf")) {
          mimeType = "application/pdf";
        }

        base64Image.value = `data:${mimeType};base64,${response}`;
      } else {
        base64Image.value = response;
      }

      // Parche específico para Android 12 o inferior: una sola ejecución
      if (
        isAndroid &&
        androidVersion <= 12 &&
        base64Image.value &&
        !android12PatchApplied.value
      ) {
        android12PatchApplied.value = true;
        // Permitir que el DOM se actualice primero
        nextTick(() => {
          // Forzar un re-render suave sin bucles
          const imgElements = document.querySelectorAll(
            'img[src*="data:image"]'
          );
          imgElements.forEach((img) => {
            // Pequeño delay para asegurar que el elemento existe
            setTimeout(() => {
              const currentSrc = (img as HTMLImageElement).getAttribute("src");
              if (currentSrc && currentSrc.includes("data:image")) {
                // Disparar un pequeño cambio para forzar re-layout
                (img as HTMLImageElement).style.display = "none";
                setTimeout(() => {
                  (img as HTMLImageElement).style.display = "";
                }, 10);
              }
            }, 50);
          });
        });
      }

      // Documento cargado, marcar como completado
      loadingFlags.value.document = true;
      checkLoadingComplete();
    },
    (err: any) => {
      showAlert({
        text: err.message || "Error al descargar documento",
        color: "error",
      });
      // Aunque falle, permitir mostrar el diálogo
      loadingFlags.value.document = true;
      checkLoadingComplete();
    }
  );
}

function attachDocument() {
  if (uploaderInput.value) {
    uploaderInput.value.click();
  }
}

async function onFileChanged(e: Event) {
  const target = e.target as HTMLInputElement;
  const selectedFile = target.files?.[0];

  if (!selectedFile) {
    return;
  }

  // Mostrar el diálogo de "Leyendo datos..." inmediatamente
  if (dlgReadingTicket.value) {
    dlgReadingTicket.value.open();
  }

  // Detectar si es archivo HEIC/HEIF
  const fileNameExt = selectedFile.name?.includes(".")
    ? selectedFile.name
        .substring(selectedFile.name.lastIndexOf("."))
        .toLowerCase()
    : "";
  const mime = (selectedFile.type || "").toLowerCase();

  const isHeicExt = fileNameExt === ".heic" || fileNameExt === ".heif";
  const isHeicMime = mime === "image/heic" || mime === "image/heif";

  if (isHeicExt || isHeicMime) {
    // Convertir HEIC a JPEG usando el composable
    const nameWithoutExt = selectedFile.name.includes(".")
      ? selectedFile.name.substring(0, selectedFile.name.lastIndexOf("."))
      : selectedFile.name;

    try {
      await heicToJpg(selectedFile, nameWithoutExt, (jpegFile) => {
        continueUploadFile(jpegFile);
      });
    } catch (error) {
      if (dlgReadingTicket.value) {
        dlgReadingTicket.value.close();
      }
      console.error("Error al convertir HEIC:", error);
      showAlert({
        text: "Error al convertir imagen HEIC. Por favor, intenta con otra imagen.",
        color: "error",
      });
    }
    return;
  }

  continueUploadFile(selectedFile);
}

async function continueUploadFile(uploadFile: File) {
  // El diálogo ya está abierto desde onFileChanged
  // No llamar dlgReadingTicket.value.open() de nuevo

  let processedFile = uploadFile;

  // Parche para Android 12 o inferior
  if (isAndroid && androidVersion <= 12) {
    try {
      const buf = await uploadFile.arrayBuffer();
      const type = (uploadFile.type || "").toLowerCase() || "image/jpeg";
      const originalName = uploadFile.name || "image";
      const hasDot = originalName.includes(".");

      // Determinar extensión según tipo
      let inferredExt = ".jpg";
      if (type.includes("png")) {
        inferredExt = ".png";
      } else if (type.includes("webp")) {
        inferredExt = ".webp";
      } else if (type.includes("gif")) {
        inferredExt = ".gif";
      }

      const baseName = hasDot
        ? originalName.substring(0, originalName.lastIndexOf("."))
        : originalName;
      const ext = hasDot
        ? originalName.substring(originalName.lastIndexOf("."))
        : inferredExt;
      const stableName = `${baseName}${ext}`;
      const stableFile = new File([new Uint8Array(buf)], stableName, {
        type: type || "image/jpeg",
        lastModified: Date.now(),
      });
      processedFile = stableFile;

      // Limpiar el input
      if (uploaderInput.value) {
        uploaderInput.value.value = "";
      }
    } catch (e) {
      console.warn(
        "No se pudo clonar el archivo para Android 12. Se continúa con el archivo original.",
        e
      );
    }
  }

  // Guardar el archivo procesado para usarlo después en save()
  file.value = processedFile;

  const formData = new FormData();
  formData.append("sheetId", String(data.value.sheetId));
  if (data.value.id) {
    formData.append("sheetItemId", String(data.value.id));
  }

  const safeName =
    processedFile && processedFile.name ? processedFile.name : "photo.jpg";
  formData.append("file", processedFile, safeName);

  expenseSheetsApi.extractExpenseSheetItemTicketInfo(
    formData,
    (response: any) => {
      if (dlgReadingTicket.value) {
        dlgReadingTicket.value.close();
      }

      // MANTENER el archivo para el guardado posterior
      // NO hacer file.value = null aquí

      if (response) {
        // Si no teníamos ID original y ahora sí, significa que se creó en esta sesión
        if (!originalItemId.value && response.item?.id) {
          wasCreatedInThisSession.value = true;
        }

        data.value = response.item;

        if (response.info && dlgConfirmTicket.value) {
          dlgConfirmTicket.value.open(response.info, () => {
            data.value.ticketNumber = response.info.ticketNumber;
            data.value.vendorVatNumber = response.info.vendorVatNumber;
            data.value.vendorName = response.info.vendorName;
            data.value.ticketDate = buildDateTime(
              response.info.date,
              response.info.time
            );
            ticketDateFormatted.value = formatLongDateTime(
              data.value.ticketDate
            );
            // Formatear valores numéricos del backend al formato del locale
            taxesPercent.value = response.info.taxesPercent
              ? formatNumberToLocale(response.info.taxesPercent)
              : null;
            taxesAmount.value = response.info.taxesAmount
              ? formatNumberToLocale(response.info.taxesAmount)
              : null;
            grossAmount.value = response.info.grossAmount
              ? formatNumberToLocale(response.info.grossAmount)
              : null;
            totalAmount.value = response.info.totalAmount
              ? formatNumberToLocale(response.info.totalAmount)
              : null;
          });
        }

        downloadDocumentContent();
      }
    },
    (err: any) => {
      if (dlgReadingTicket.value) {
        dlgReadingTicket.value.close();
      }
      showAlert({
        text: err.message || "Error al procesar el archivo",
        color: "error",
      });
    }
  );
}

function deleteExpenseSheetItemDocument() {
  isLoading.value = true;

  expenseSheetsApi.deleteExpenseSheetItemDocument(
    data.value.id,
    (response: any) => {
      isLoading.value = false;
      // Solo actualizar los campos relacionados con el documento, no todo el objeto
      // para preservar los cambios del usuario en el formulario
      data.value.documentPath = response.documentPath;
      data.value.documentName = response.documentName;
      base64Image.value = null;
      // Limpiar también el archivo local para evitar inconsistencias
      file.value = null;
      // Limpiar el input de archivo
      if (uploaderInput.value) {
        uploaderInput.value.value = "";
      }
    },
    (err: any) => {
      isLoading.value = false;
      console.error("Error al eliminar documento:", err);
      showAlert({
        text: err.message || "Error al eliminar documento",
        color: "error",
      });
    }
  );
}

function validateForm(): boolean {
  let isValid = true;

  // Resetear todos los errores
  for (const key of Object.keys(formErrors.value)) {
    formErrors.value[key] = null;
  }

  // Validaciones básicas de campos requeridos
  if (!data.value.sheetId) {
    formErrors.value.sheetId = t(
      "wkt.client.common.validation-utils.required_field"
    );
    isValid = false;
  }
  if (!data.value.categoryId) {
    formErrors.value.categoryId = t(
      "wkt.client.common.validation-utils.required_field"
    );
    isValid = false;
  }
  if (!formatType.value) {
    formErrors.value.formatType = t(
      "wkt.client.common.validation-utils.required_field"
    );
    isValid = false;
  }
  if (!paymentMethod.value) {
    formErrors.value.paymentMethod = t(
      "wkt.client.common.validation-utils.required_field"
    );
    isValid = false;
  }
  if (!data.value.ticketDate) {
    formErrors.value.ticketDate = t(
      "wkt.client.common.validation-utils.required_field"
    );
    isValid = false;
  }
  if (!data.value.expenseDescription) {
    formErrors.value.expenseDescription = t(
      "wkt.client.common.validation-utils.required_field"
    );
    isValid = false;
  }

  // Validar totalAmount (requerido + decimal + mayor que 0)
  const requiredResult = validationRules.required(totalAmount.value);
  if (requiredResult !== true) {
    formErrors.value.totalAmount = requiredResult;
    isValid = false;
  } else {
    const decimalResult = validationRules.decimalSeparator(
      totalAmount.value || ""
    );
    if (decimalResult !== true) {
      formErrors.value.totalAmount = decimalResult;
      isValid = false;
    } else if (!validationRules.isGreaterThanZero(totalAmount.value || "")) {
      formErrors.value.totalAmount = t(
        "wkt.client.common.validation-utils.must_be_greater_than_zero"
      );
      isValid = false;
    }
  }

  // Validaciones para facturas
  if (onlyForInvoice.value) {
    // Validar CIF/NIF (solo requerido)
    if (!data.value.vendorVatNumber) {
      formErrors.value.vendorVatNumber = t(
        "wkt.client.common.validation-utils.required_field"
      );
      isValid = false;
    }

    if (!data.value.vendorName) {
      formErrors.value.vendorName = t(
        "wkt.client.common.validation-utils.required_field"
      );
      isValid = false;
    }

    // Validar taxesPercent (requerido + decimal sin límite + mayor que 0)
    const taxesPercentReq = validationRules.required(taxesPercent.value);
    if (taxesPercentReq !== true) {
      formErrors.value.taxesPercent = taxesPercentReq;
      isValid = false;
    } else {
      const taxesPercentDecimal = validationRules.decimalSeparatorNoLimit(
        taxesPercent.value || ""
      );
      if (taxesPercentDecimal !== true) {
        formErrors.value.taxesPercent = taxesPercentDecimal;
        isValid = false;
      } else if (!validationRules.isGreaterThanZero(taxesPercent.value || "")) {
        formErrors.value.taxesPercent = t(
          "wkt.client.common.validation-utils.must_be_greater_than_zero"
        );
        isValid = false;
      }
    }

    // Validar taxesAmount (requerido + decimal + mayor que 0)
    const taxesAmountReq = validationRules.required(taxesAmount.value);
    if (taxesAmountReq !== true) {
      formErrors.value.taxesAmount = taxesAmountReq;
      isValid = false;
    } else {
      const taxesAmountDecimal = validationRules.decimalSeparator(
        taxesAmount.value || ""
      );
      if (taxesAmountDecimal !== true) {
        formErrors.value.taxesAmount = taxesAmountDecimal;
        isValid = false;
      } else if (!validationRules.isGreaterThanZero(taxesAmount.value || "")) {
        formErrors.value.taxesAmount = t(
          "wkt.client.common.validation-utils.must_be_greater_than_zero"
        );
        isValid = false;
      }
    }

    // Validar grossAmount (requerido + decimal + mayor que 0)
    const grossAmountReq = validationRules.required(grossAmount.value);
    if (grossAmountReq !== true) {
      formErrors.value.grossAmount = grossAmountReq;
      isValid = false;
    } else {
      const grossAmountDecimal = validationRules.decimalSeparator(
        grossAmount.value || ""
      );
      if (grossAmountDecimal !== true) {
        formErrors.value.grossAmount = grossAmountDecimal;
        isValid = false;
      } else if (!validationRules.isGreaterThanZero(grossAmount.value || "")) {
        formErrors.value.grossAmount = t(
          "wkt.client.common.validation-utils.must_be_greater_than_zero"
        );
        isValid = false;
      }
    }

    if (!data.value.ticketNumber) {
      formErrors.value.ticketNumber = t(
        "wkt.client.common.validation-utils.required_field"
      );
      isValid = false;
    }
  }

  return isValid;
}

function save() {
  if (!validateForm()) {
    return;
  }

  const formattedTaxesAmount = taxesAmount.value
    ? formatCurrency(taxesAmount.value)
    : null;
  const formattedGrossAmount = grossAmount.value
    ? formatCurrency(grossAmount.value)
    : null;
  const formattedTotalAmount = totalAmount.value
    ? formatCurrency(totalAmount.value)
    : null;
  const formattedTaxesPercent = taxesPercent.value
    ? formatCurrency(taxesPercent.value)
    : null;

  data.value.formatType = formatType.value.id;
  data.value.paymentMethod = paymentMethod.value.id;

  isLoading.value = true;

  const formData = new FormData();

  // Agregar archivo si existe
  if (file.value) {
    formData.append("file", file.value, file.value.name);
  }

  // Campos obligatorios
  if (data.value.id) {
    formData.append("id", String(data.value.id));
  }
  formData.append("sheetId", String(data.value.sheetId));
  formData.append("categoryId", String(data.value.categoryId));
  formData.append("formatType", String(data.value.formatType));
  formData.append("paymentMethod", String(data.value.paymentMethod));
  formData.append("ticketDate", String(data.value.ticketDate));
  formData.append("expenseDescription", data.value.expenseDescription);

  if (formattedTotalAmount) {
    formData.append(
      "totalAmount",
      String(Number(formattedTotalAmount.replace(",", ".")))
    );
  }

  // Campos solo para factura
  if (onlyForInvoice.value) {
    formData.append("vendorName", data.value.vendorName);
    formData.append("vendorVatNumber", data.value.vendorVatNumber);
    formData.append("ticketNumber", data.value.ticketNumber);

    if (formattedTaxesPercent) {
      formData.append(
        "taxesPercent",
        String(Number(formattedTaxesPercent.replace(",", ".")))
      );
    }
    if (formattedTaxesAmount) {
      formData.append(
        "taxesAmount",
        String(Number(formattedTaxesAmount.replace(",", ".")))
      );
    }
    if (formattedGrossAmount) {
      formData.append(
        "grossAmount",
        String(Number(formattedGrossAmount.replace(",", ".")))
      );
    }
    if (data.value.description && data.value.description.trim().length > 0) {
      formData.append("description", data.value.description);
    }
  }

  expenseSheetsApi.storeExpenseSheetItem(
    formData,
    handleSaveSuccess,
    handleSaveError
  );
}

function handleSaveSuccess(response: any) {
  isLoading.value = false;
  // Al guardar exitosamente, ya no es un item temporal
  wasCreatedInThisSession.value = false;

  if (response) {
    showAlert({
      text: t("wkt.client.expensesheets.edit-item-dialog.save_ok"),
      color: "success",
    });
  } else {
    showAlert({
      text: t("wkt.client.expensesheets.edit-item-dialog.save_error"),
      color: "error",
    });
  }
  showDialog.value = false;
  if (msgCbUpdated.value) {
    msgCbUpdated.value();
  }
}

function handleSaveError(err: any) {
  isLoading.value = false;
  console.error("Error al guardar item:", err);
  showAlert({
    text: err.message || "Error al guardar",
    color: "error",
  });
}

function buildDateTime(date: string, time: string): number | null {
  if (!date || date.length === 0) {
    return null;
  }

  let processedTime = time;
  if (!time || time.length === 0) {
    processedTime = "00:00";
  }

  const [day, month, year] = date.split("/");
  const [hours, minutes] = processedTime.split(":");

  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes)
  ).getTime();
}

function confirmDelete() {
  if (dlgConfirm.value) {
    dlgConfirm.value.confirm(data.value);
  }
}

function deleteItem(notify: boolean) {
  // Solo eliminar si el id es válido
  if (
    !data.value.id ||
    typeof data.value.id !== "number" ||
    data.value.id <= 0
  ) {
    showDialog.value = false;
    return;
  }
  isLoading.value = true;
  expenseSheetsApi.deleteExpenseSheetItemById(
    data.value.id,
    (res) => {
      isLoading.value = false;
      showDialog.value = false;
      if (notify) {
        if (res) {
          showAlert({
            text: t("wkt.client.expensesheets.edit-item-dialog.delete_ok"),
            color: "success",
          });
        } else {
          showAlert({
            text: t("wkt.client.expensesheets.edit-item-dialog.delete_error"),
            color: "error",
          });
        }
        if (msgCbDeleted.value) {
          msgCbDeleted.value();
        }
      }
    },
    (err: any) => {
      isLoading.value = false;
      console.error("Error al eliminar item:", err);
      showAlert({
        text: err.message || "Error al eliminar",
        color: "error",
      });
    }
  );
}

// Lifecycle
onMounted(() => {
  checkMobile();
  window.addEventListener("resize", checkMobile);
});

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile);
});

// Exponer métodos para uso externo
defineExpose({
  open,
  close,
});
</script>

<style scoped>
/* Contenido */
.item-content {
  padding: 1rem;
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Grid del formulario */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-field.full-width {
  grid-column: 1 / -1;
}

.field-label {
  font-weight: 500;
  color: #424242;
  font-size: 14px;
}

.required {
  color: #d32f2f;
  margin-left: 2px;
}

/* Sección de adjuntar (móvil) */
.attach-section-mobile {
  margin-top: 1.5rem;
  padding: 1rem;
  border: 1px dashed var(--surface-300);
  border-radius: var(--border-radius);
  text-align: center;
}

.attach-title {
  margin: 0 0 1rem 0;
  color: #000;
  font-size: 1.1em;
}

/* Documento adjunto */
.document-section {
  margin-top: 1.5rem;
}

.document-title {
  margin: 0 0 1rem 0;
  color: #000;
  font-size: 1.1em;
}

.document-preview {
  display: flex;
  justify-content: center;
  margin: 1.5rem;
}

.document-image {
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 5px;
  max-width: 100%;
}

.document-image.p-image-preview deep:img {
  max-width: 300px !important;
  height: auto !important;
}

.document-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 50%;
  margin: 0 auto;
}

.document-name {
  /*flex: 1;*/
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Footer */
.dialog-footer-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
}

.spacer {
  flex: 1;
}

/* Input oculto */
.hidden-input {
  display: none;
}

/* Responsive - móvil */
@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .item-content {
    padding: 0;
    height: calc(100vh - 175px);
    max-height: 100%;
  }
}

/* Previsualización de imagen */
.image-preview-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.image-preview {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: var(--border-radius);
}

/* Overlay de previsualización a pantalla completa */
.image-preview-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  border-radius: inherit;
}

.image-preview-full {
  max-width: 95%;
  max-height: 100%;
  object-fit: contain;
  display: block;
}
</style>

<!-- Global override para toolbar de previsualización (no scoped) -->
<style>
/* Forzar toolbar del preview de PrimeVue a 50px desde la parte superior */
.p-image-toolbar {
  top: 50px !important;
}
</style>
